define('Model.List', [
	'utils/util',
	'filter',
	'inherit',
	'RPC',
	'Emitter'
], function (
	/** util */util,
	/** Function */filter,
	/** Function */inherit,
	/** RPC */RPC,
	/** Emitter */Emitter
) {
	"use strict";


	/**
	 * «Обещание» с ссылкой на список моделей, которые будут загружены
	 * @typedef  {Object}  ModelListPromise
	 * @property {Model.List} list
	 * @property {Function} then
	 * @property {Function} done
	 * @property {Function} fail
	 * @property {Function} always
	 */



	var array = [],

		array_push = array.push,
		array_join = array.join,
		array_sort = array.sort,
		array_slice = array.slice,
		array_splice = array.splice,
		array_indexOf = array.indexOf,
		array_some = array.some,
		array_every = array.every,
		array_reduce = array.reduce,

		_allModelEvents = 'sync change remove destroy',

		stringifyJSON = JSON.stringify,

		_compareAttr = function (/* Model */a, /* Model **/b, /* string */attr, /* boolean */desc) {
			var aVal = a.get(attr),
				bVal = b.get(attr),
				res = aVal - bVal;

			if (res !== res) {
				if (aVal && aVal.localeCompare) {
					res = aVal.localeCompare(bVal);
				} else {
					res = (aVal == bVal) ? 0 : (aVal > bVal ? 1 : -1);
				}
			}

			return res * (desc ? -1 : 1);
		}
	;


	/**
	 * Список моделей (коллекция)
	 * @class Model.List
	 * @constructs Model.List
	 * @mixes  Emitter
	 * @mixes  inherit
	 * @param  {Model[]}    [models]  массив моделей
	 * @param  {boolean}    [lazy]    ленивая инициализация
	 */
	function List(models, lazy) {
		this._index = {};
		this._safedStore = {};
		this.length = 0;

		// Метод должен быть уникальный для каждого экземпляра класса
		this._debounceTriggerUpdate = util.debounce(function () {
			this.trigger('update', this);
		}, 0);

		if (models instanceof Array || models instanceof List) {
			this.set(models, true);
		}
		else if (lazy) {
			this.on = this._lazyOn;
			this.get = this._lazyGet;
		}
	}


	List.fn = List.prototype = /** @lends Model.List# */ {
		constructor: List,


		/**
		 * Как сортировать коллекцию, подробнее смотрите метод `sort`
		 * @type {string|object|function}
		 */
		comparator: null,


		/**
		 * Связанная модель
		 * @type {Model}
		 */
		Model: null,


		/**
		 * Количество моделей в коллекции
		 * @type {number}
		 */
		length: 0,

		/**
		 * Привязаны ли модели в этом списке к глобальному списку экзепляров (Model.all)
		 * @type {boolean}
		 */
		local: false,

		/**
		 * Дополнительные индексы
		 * @type {object}
		 * @private
		 */
		_indexes: null,


		/**
		 * Ленивая инициазиция слушателей событий
		 * @param   {string}    events
		 * @param   {Function}  fn
		 * @returns {Model.List}
		 * @private
		 */
		_lazyOn: function (events, fn) {
			var on = Emitter.fn.on,
				i = this.length;

			this.on = on; // возвращаем метод

			while (i--) {
				this[i].on(_allModelEvents, this);
			}

			return on.call(this, events, fn);
		},


		/**
		 * Ленивая инициазиция метода индекса
		 * @param   {*}    id
		 * @returns {Model}
		 * @private
		 */
		_lazyGet: function (id) {
			var get = List.fn.get,
				i = this.length,
				model,
				_index = this._index
			;

			this.get = get; // возвращаем метод

			while (i--) {
				model = this[i];
				_index[model.id] = _index[model.cid] = model;
				(this._addToIndex !== void 0) && this._addToIndex(model);
			}

			return get.call(this, id);
		},


		/**
		 * Подготовить модель
		 * @param   {Object}  attrs
		 * @param   {options} options
		 * @returns {Model}
		 * @private
		 */
		_prepareModel: function (attrs, options) {
			var Model = this.Model,
				model = attrs,
				all = Model.all,
				id
			;

			if (!(attrs instanceof Model)) {
				// попробуем достать из глобальной коллекции
				id = attrs.cid || attrs[Model.fn.idAttr];
				model = this.get(id) || (!this.local && all.get(id));

				if (!model) {
					// Создаем модель
					model = new Model(attrs);

					!this.local && all.set([model]);
				}
				else {
					// просто обновляем
					model.set(attrs, options);
				}
			}

			return model;
		},


		/**
		 * Обработка и делигирование события от модели
		 * @param  {Emitter.Event}  evt
		 * @param  {Model}  model
		 * @private
		 */
		handleEvent: function (evt, model) {
			var type = evt.type;

			if (type === 'remove' || type === 'destroy') {
				this.remove(model);
			}
			else { // Метод `remove` сам испускает события
				(model.id !== null) && (this._index[model.id] = model); // переиндексируем

				evt.list = this;
				this.trigger(evt, model);
			}

			this._changed = type === 'change';

			if (this._changing !== true) { // Выставляется в `set`
				this._debounceTriggerUpdate();
			}
		},


		/**
		 * Перебор моделей
		 * @param   {Function} iterator
		 * @param   {*}  thisArg
		 * @returns {Model.List}
		 */
		each: function (iterator, thisArg) {
			for (var i = 0, n = this.length; i < n; i++) {
				iterator.call(thisArg, this[i], i, this);
			}
			return this;
		},


		/**
		 * Вернет массив, полученный преобразованием каждой модели в функции iterator
		 * @param  {Function} iterator
		 * @param  {*} thisArg
		 * @return {Array}
		 */
		map: function (iterator, thisArg) {
			var retArr = [];

			for (var i = 0, n = this.length; i < n; i++) {
				retArr.push(iterator.call(thisArg, this[i], i, this));
			}

			return retArr;
		},


		/**
		 * Проверяет, удовлетворяет ли хоть какой-нибудь элемент массива условию, заданному в передаваемой функции.
		 * @param   {Function} callback
		 * @param   {*} [thisArg]
		 * @returns {boolean}
		 */
		some: function (callback, thisArg) {
			return array_some.call(this, callback, thisArg || this);
		},


		/**
		 * Проверяет, удовлетворяют ли все элементы массива условию, заданному в передаваемой функции.
		 * @param   {Function} callback
		 * @param   {*} [thisArg]
		 * @returns {boolean}
		 */
		every: function (callback, thisArg) {
			return array_every.call(this, callback, thisArg || this);
		},


		/**
		 * Применяет функцию `callback` по очереди к каждому элементу массива слева направо,
		 * сохраняя при этом промежуточный результат.
		 * @param   {Function} callback
		 * @param   {*} [initValue]
		 * @returns {*}
		 */
		reduce: function (callback, initValue) {
			return array_reduce.apply(this, arguments);
		},


		/**
		 * Посчитать сумму
		 * @param   {string} attr
		 * @returns {number}
		 */
		sum: function (attr) {
			var sum = 0;

			this.each(function (model) {
				sum += model.get(attr);
			});

			return sum;
		},


		/**
		 * Посчитать среднее арифметическое
		 * @param   {string} attr
		 * @returns {number}
		 */
		avg: function (attr) {
			return this.sum(attr) / this.length;
		},


		/**
		 * Вернет новую коллекцию, состоящую из моделей,
		 * для которых итератор возвращает истинное значение
		 * @param  {Function|Object} iterator   итератор, либо объект "имя аттрибута" => "зачение"
		 * @param  {*}               [thisArg]  `this` для итератора
		 * @param  {boolean}         [once]     вернуть первое найденное значение
		 * @returns {Model.List|Model|undefined}
		 */
		filter: function (iterator, thisArg, once) {
			var list = new (this.constructor)(null, true),
				i = 0,
				n = this.length,
				model,
				length = 0,
				isWhere
			;

			iterator = filter.createIterator(iterator, thisArg);
			isWhere = iterator.isWhere;

			for (; i < n; i++) {
				model = this[i];

				if (isWhere === true) {
					/* istanbul ignor else */
					if (iterator(model.attributes, model)) {
						list[length++] = model;
					}
				}
				else if (iterator(model, i, this)) {
					list[length++] = model;
				}

				if (length === 1 && once) {
					return list[0];
				}
			}

			if (once) {
				return;
			}

			list.length = length;

			return list;
		},


		/**
		 * Получить первую модель из коллекции
		 * @returns {Model}
		 */
		first: function () {
			return this[0];
		},


		/**
		 * Получить последнию модель из коллекции
		 * @returns {Model}
		 */
		last: function () {
			return this[this.length - 1];
		},


		/**
		 * Найти модель (см. filter)
		 * @param   {Function|Object}  iterator
		 * @param   {*}                [thisArg]
		 * @returns {Model}
		 */
		find: function (iterator, thisArg) {
			return this.filter(iterator, thisArg, true);
		},


		/**
		 * Получить массив значений по имени свойства
		 * @param   {string}  attr  имя свойства модели
		 * @returns {Array}
		 */
		pluck: function (attr) {
			return this.map(function (model) {
				return model.get(attr);
			});
		},


		/**
		 * Вернет `true`, если коллекция содержит элемент model
		 * @param   {*}  model   модель, идентификатор или cid
		 * @return {boolean}
		 */
		contains: function (model) {
			return !!this.get(model);
		},


		/**
		 * Получить index модели
		 * @param   {Model|object|string|number}  [model]   модель, объект, id или cid
		 * @returns {number}
		 */
		indexOf: function (model) {
			if (!(model instanceof Object)) {
				model = { id: model, cid: model };
			}

			var idx = this.length,
				_model;

			while (idx--) {
				_model = this[idx];

				if (_model.id === model.id || _model.cid === model.cid) {
					return idx;
				}
			}

			return -1;
		},


		/**
		 * Сортировать
		 * @param   {Object|Function}  attrs       аттрибут, объект { attr: desc(boolean) } или функция
		 * @param   {Object|boolean}   [options]   возможные опции: silent, reverse, clone
		 * @returns {Model.List}
		 */
		sort: function (attrs, options) {
			if (!attrs) {
				attrs = this.comparator || this.Model.fn.idAttr;
			}


			var _this = this,
				typeOf = typeof attrs,
				comparator,
				silent,
				reverse;


			if (options) {
				silent = options === true || options.silent;
				reverse = options.reverse;

				if (options.clone) {
					_this = _this.clone();
				}
			}


			if (typeOf === 'string') {
				// Сортировка по аттрибуту
				comparator = function (a, b) {
					return _compareAttr(a, b, attrs, reverse);
				};
			}
			else if (typeOf === 'object') {
				// сортировка по нескольким аттрибутам
				var keys = Object.keys(attrs),
					keysLength = keys.length;

				comparator = function (a, b) {
					var i = 0,
						res = 0;

					for (; i < keysLength; i++) {
						res = _compareAttr(a, b, keys[i], attrs[keys[i]] ^ reverse);

						if (res !== 0) {
							return res;
						}
					}

					return res;
				};
			}
			else {
				// Передана функция сравнения
				comparator = function (a, b) {
					return attrs.call(this, a, b) * (reverse ? -1 : 1);
				};
			}

			// Сортируем
			array_sort.call(_this, comparator);

			!silent && _this.trigger('sort', _this);

			return _this;
		},


		/**
		 * Получить модель по индексу
		 * @param   {number}  index
		 * @returns {Model|undefined}
		 */
		eq: function (index) {
			return this[index];
		},


		/**
		 * Получить модель по id
		 * @param   {*}  id   идентификатор, cid или модель
		 * @returns {Model|undefined}
		 */
		get: function (id) {
			/* jshint eqnull:true */
			return (id == null)
				? void 0
				: this._index[id.cid || id[this.Model.fn.idAttr] || id]
			;
		},


		/**
		 * Получить модель по id даже если её нет
		 * @param   {*}  id   идентификатор или cid
		 * @returns {Model}
		 */
		getSafe: function (id) {
			var model = this.get(id);
			var store = this.Model.all._safedStore;

			/* istanbul ignore else */
			if (model === void 0) {
				id = id && (id[this.Model.fn.idAttr] || id);

				model = store[id];

				if (model === void 0) {
					model = new (this.Model)({id: id});
					store[id] = model;
				}
			}

			return model;
		},


		/**
		 * Создать модель и добавить в коллекцию
		 * @param   {Object}  attrs  свойства модели
		 * @param   {Object}  [options]
		 * @returns {Model}
		 */
		create: function (attrs, options) {
			var model = this._prepareModel(attrs);
			this.set([model], options);
			return model;
		},


		/**
		 * Установить новый список моделей
		 * @param   {Model[]}  models  массив свойств или моеделей
		 * @param   {Object}   [options]   опции (clone: false, reset: false, silent: false, sort: boolean|preset, merge: true, sync: false)
		 * @returns {Model.List}
		 */
		set: function (models, options) {
			var i,
				n,
				evt = new Emitter.Event('add'),
				cid,
				model,
				_models, // предудущий список моделей

				_index = this._index,
				length = this.length,
				newLength,

				clone = false,
				merge = true,
				reset = false,
				silent = false,
				changed = false,

				modelOptions = {},

				sort = !!this.comparator,
				sorted = false
			;


			// Клонируем массив, потому что дальше мы будем его модифицировать
			models = (models && models.slice) ? models.slice() : [];
			newLength = models.length;

			if (options) {
				clone = options.clone;
				merge = options.merge !== false;
				reset = options.reset;
				silent = options.silent || options === true;

				if (options.sort !== void 0) {
					sort = options.sort;
				}

				modelOptions.sync = options.sync;
				modelOptions.clean = options.clean;
			}


			if (reset) {
				// Сбрасываем все модели
				_models = array_splice.call(this, 0, length);
				i = length;

				reset = length > 0 || newLength > 0;
				changed = reset;

				// Отписываемся от событий модели
				while (i--) {
					_models[i].off(_allModelEvents, this);
				}

				// Сбрасываем index и длину
				length = 0;
				_index = this._index = {};
			}


			for (i = 0, n = newLength; i < n; i++) {
				this._changed = false; // Выставляет в `handleEvent` при изменении моделей (оптимизация)
				this._changing = true; // Проверяется в `handleEvent` при изменении моделей (оптимизация)

				model = this._prepareModel(models[i], modelOptions);

				cid = model.cid;
				changed = changed || this._changed;
				models[i] = model;

				// Проверяем в индексе
				if (_index[cid] === void 0) {
					// Добавялем в индекс
					_index[cid] = model;
					(model.id !== null) && (_index[model.id] = model);
					(this._addToIndex !== void 0) && this._addToIndex(model);

					// Добавляем в коллекцию
					changed = true;
					this[length] = model;
					this.length = ++length;

					// Подписываемся на события
					model.on(_allModelEvents, this);

					if (!silent) {
						// Событие "add"
						evt.list = this;
						evt.target = model;
						this.trigger(evt, model);
					}
				}


				// Сохранить порядок в котором переданны элементы
				if (sort === 'preset' && this[i] !== model) {
					sorted = true;

					this[this.indexOf(model)] = this[i];
					this[i] = model;
				}
			}


			if (merge === false) {
				// Проходимся по моделяем и удаляем которых нет
				i = length;

				while (i--) {
					if (models.indexOf(this[i]) === -1) {
						changed = true;
						this.remove(this[i]);
					}
				}
			}


			if (changed && sort === true && !sorted) {
				// Сортируем коллекцию, если были изменения
				sorted = true;
				this.sort(null, { silent: true });
			}

			this._changing = false;

			if (!silent) { // Без событий
				sorted && this.trigger('sort', this);
				reset && this.trigger({ type: 'reset', models: this, previousModels: _models }, this);

				if (sorted || changed) {
					this._debounceTriggerUpdate.cancel();
					this.trigger('update', this);
				}
			}


			if (clone) {
				return new (this.constructor)(models);
			}

			return this;
		},


		/**
		 * Преобразовать в массив
		 * @returns {Model[]}
		 */
		toArray: function () {
			return array_slice.call(this);
		},


		/**
		 * Преобразовать в JSON
		 * @param   {boolean}  [ref]  вернуть ссылку на аттрибуты модели
		 * @returns {Object[]}
		 */
		toJSON: function (ref) {
			return this.map(function (model) {
				return model.toJSON(ref);
			});
		},


		/**
		 * Обрезать, возвращает новую коллекцию
		 * @param   {number}  [start]
		 * @param   {number}  [end]
		 * @returns {Model.List}
		 */
		slice: function (start, end) {
			var array = start === void 0 ? this : array_slice.call(this, start, end === void 0 ? this.length : end),
				i = array.length,
				newList = new (this.constructor)(null, true)
			;

			while (i--) {
				newList[i] = array[i];
			}

			newList.length = array.length;

			return newList;
		},


		/**
		 * Сбросить коллекуию
		 * @param   {Array}  [models]
		 * @param   {Object} [options]
		 * @returns {Model.List}
		 */
		reset: function (models, options) {
			options = options || {};

			if (options === true) {
				options = { silent: true };
			}

			options.reset = true;
			options.clone = false;

			return this.set(models, options);
		},


		/**
		 * Удалить модель из коллекции
		 * @param   {Model}  model
		 * @returns {Model.List}
		 */
		remove: function (model) {
			var evt,
				idx = array_indexOf.call(this, model);

			/* istanbul ignore else */
			if (idx !== -1) {
				model.off(_allModelEvents, this);
				array_splice.call(this, idx, 1);

				delete this._index[model.id];
				delete this._index[model.cid];

				evt = new Emitter.Event('remove');
				evt.list = this;
				evt.target = model;

				this.trigger(evt, model);

				if (this._changing !== true) { // Выставляется в `set`
					this._debounceTriggerUpdate();
				}
			}

			return this;
		},


		/**
		 * Сохранить изменения единой пачкой
		 * @param   {*} [attr]
		 * @param   {*} [value]
		 * @param   {*} [options]
		 * @returns {Promise}
		 */
		save: function (attr, value, options) {
			return RPC.batch(function () {
				this.each(function (model) {
					model.save(attr, value, options);
				});
			}.bind(this));
		},


		/**
		 * Удалить всю коллекцию
		 * @param   {Object}  [query] дополнительные параметры запроса
		 * @retruns {Promise}
		 */
		drop: function (query) {
			return RPC.batch(function () {
				this.each(function (model) {
					model.remove(query);
				});
			}.bind(this));
		},


		/**
		 * Уничтожить коллекцию и модели в ней
		 * @returns {Model.List}
		 */
		destroy: function () {
			var i = this.length;

			while (i--) {
				/* jshint expr:true */
				this[i] && this[i].destroy();
			}

			return this;
		},


		/**
		 * Получить или обновить список
		 * @param   {Object} [query]   параметры запроса
		 * @param   {Object} [options] опции (reset: false, force: false)
		 * @returns {ModelListPromise}
		 */
		fetch: function (query, options) {
			options = options || {};

			var list = this,
				Model = list.Model,
				all = Model.all,
				promise = Model.fetchData(query, null, options.force)
					.then(function (array) {
						!list.local && all.set(array); // глобальный список
						list[options.reset ? 'reset' : 'set'](array, options);

						return list;
					})
					.fail(function (err) {
						list.emit('error', err);
					})
			;

			promise.list = list;

			return promise;
		},


		/**
		 * Клонировать коллекцию
		 * @returns {Model.List}
		 */
		clone: function () {
			return this.slice();
		}
	};


	/**
	 * @name  Model.List#forEach
	 * @alias each
	 * @method
	 */
	List.fn.forEach = List.fn.each;


	/**
	 * @name  Model.List#where
	 * @alias filter
	 * @method
	 */
	List.fn.where = List.fn.filter;


	/**
	 * @name  Model.List#findWhere
	 * @alias find
	 * @method
	 */
	List.fn.findWhere = List.fn.find;


	// Подмещиваем методы Emitter'а
	Emitter.apply(List.fn);


	// Подмещиваем наследование
	inherit.apply(List);


	/**
	 * Добавить индекс для быстрого доступа к модели/моделям
	 * @param    {string}  attr  имя свойства
	 * @param    {boolean} [uniq]  уникальный индекс
	 * @methodOf List
	 */
	List.addIndex = function (attr, uniq) {
		var _indexes = this.fn._indexes;

		/* istanbul ignore else */
		if (!_indexes) {
			_indexes = this.fn._indexes = [];
		}

		_indexes.push({
			attr: attr,
			uniq: uniq
		});

		/* jshint evil:true */
		this.fn._addToIndex = Function('model', _indexes.map(function (index) {
			var attr = stringifyJSON(index.attr),
				value = attr.indexOf('.') > -1
							? 'model.get(' + attr + ')'
							: 'model.attributes[' + attr + ']';

			/* istanbul ignore else */
			if (index.uniq) {
				return 'this._index[' + value + '] = model;';
			} else {
				throw "todo: Нужно думать";
			}
		}).join('\n'));
	};

	/**
	 * Получить массив значений по имени свойства из массива или списка моделей
	 * @param    {model.List|Model[]}	list	список моделей
	 * @param    {string} property свойство
	 */
	List.pluck = function (list, property) {

		return List.fn.pluck.call(list, property);
	};

	// Export
	List.version = '0.13.0';
	return List;
});
